import React, { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import DivisionCard from "@/components/DivisionCard";
import FAQ from "@/components/FAQ";
import { DIVISIONS, INDUSTRIES } from "@/constants/site";
import { useQuote } from "@/components/QuoteProvider";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";
import * as Icons from "lucide-react";
import {
  ArrowRight, ShieldCheck, Truck, HeadphonesIcon, Award, MapPin, Factory,
  FileCheck2, Droplets, Magnet, Lightbulb, PaintBucket, Wrench, Phone, MessageCircle, FileDown,
  CheckCircle2, Building2, IndianRupee, Clock,
} from "lucide-react";

const IMG = {
  ndt: "/products/cat-dpt.jpg",
  zinc: "/products/cat-zinc.jpg",
  bessey: "/products/cat-bessey.jpg",
  bearings: "/products/cat-bearings.jpg",
};

const TRUST = [
  { icon: Award, text: "Authorised Distributor of 3AK Chemie Pvt. Ltd." },
  { icon: Wrench, text: "Standard & Customised NDT Solutions" },
  { icon: HeadphonesIcon, text: "Technical Product Selection Assistance" },
  { icon: Truck, text: "Bulk Industrial Supply" },
  { icon: Clock, text: "Quick Quotation Response" },
  { icon: IndianRupee, text: "GST Billing" },
  { icon: MapPin, text: "Telangana & Andhra Pradesh Coverage" },
  { icon: ShieldCheck, text: "Dedicated Customer Support" },
];

// Industries strip shown inside the hero band (matches reference)
const HERO_INDUSTRIES = [
  { name: "Aerospace & Defence", icon: "Plane" },
  { name: "Heavy Engineering", icon: "Wrench" },
  { name: "Fabrication & PEB", icon: "Hammer" },
  { name: "Railways", icon: "Train" },
  { name: "Power", icon: "Zap" },
  { name: "Manufacturing", icon: "Factory" },
  { name: "Infrastructure", icon: "Building2" },
];

/**
 * Angled parallelogram panel used in the hero.
 * Uses clip-path to create the leaning-right shape from the reference.
 * lightBg = true → renders image on white so bearings pop against the light grey.
 */
/**
 * Renders a pre-composed hero panel image (parallelogram shape + label strip baked in).
 */
function HeroPanelImage({ to, src, alt, testId, wrapClass = "" }) {
  return (
    <Link
      to={to}
      className={`relative block flex-1 h-full ${wrapClass}`}
      data-testid={testId}
      aria-label={alt}
    >
      <img
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        className="w-full h-full object-cover object-center hover:opacity-95 transition-opacity"
      />
    </Link>
  );
}

const FEATURES = [
  {
    icon: Droplets, color: "#0F2747",
    title: "Dye Penetrant Testing Materials",
    items: ["Cleaners & Removers", "Red Visible & Fluorescent Penetrants", "Solvent & Water-Washable Developers"],
  },
  {
    icon: Magnet, color: "#0F2747",
    title: "Magnetic Particle Inspection",
    items: ["Magnetic Inks & Powders", "Fluorescent Magnetic Particles", "White Contrast Background Paints"],
  },
  {
    icon: Lightbulb, color: "#0F2747",
    title: "UV Inspection Equipment",
    items: ["UV-A Handheld Lamps", "Lux & UV Intensity Meters", "Reference Test Blocks & Accessories"],
  },
  {
    icon: PaintBucket, color: "#475569",
    title: "KLYDE Zinc Materials",
    items: ["Cold Galvanizing Sprays", "Zinc-Rich Primers & Coatings", "Industrial Protective Coatings"],
  },
  {
    icon: Icons.Disc3, color: "#1E293B",
    title: "Industrial Bearings",
    items: ["Ball Bearings", "Roller Bearings", "Pillow Block Bearings", "Linear Bearings", "Bearing Housings & Accessories"],
    isBearings: true,
    note: "Thousands of bearing sizes available. Contact us with your bearing number or application.",
    cta: "Enquire for Bearings",
  },
];

export default function HomePage() {
  const { openQuote } = useQuote();
  const { settings } = useOutletContext() || { settings: {} };
  const s = settings || {};
  const phone = (s.phone || "").replace(/\s/g, "");
  const wa = (s.whatsapp || "").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${wa}?text=${encodeURIComponent("Hello SSK Enterprises, I need help selecting the right product.")}`;

  useSeo({
    title: "Mr Chemie NDT Products, Cold Galvanizing Zinc, Industrial Bearings & BESSEY Clamps | SSK Enterprises",
    description:
      "SSK Enterprises is the Authorised Distributor of 3AK Chemie Pvt. Ltd. for MR Chemie NDT materials and inspection solutions. We also supply KLYDE cold galvanizing zinc products, industrial bearings and BESSEY clamping and cutting tools across Telangana and Andhra Pradesh.",
    ogTitle: "Mr Chemie NDT Products, Cold Galvanizing Zinc, Industrial Bearings & BESSEY Clamps",
    ogDescription:
      "SSK Enterprises is the Authorised Distributor of 3AK Chemie Pvt. Ltd. for MR Chemie NDT materials and inspection solutions. We also supply KLYDE cold galvanizing zinc products, industrial bearings and BESSEY clamping and cutting tools across Telangana and Andhra Pradesh.",
    path: "/",
    image: "https://sskenterprise.com/og-cover.jpg",
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }])],
  });

  return (
    <>
      {/* HERO — matches reference: left content + 3 angled horizontal panels */}
      <section className="relative bg-white overflow-hidden" data-testid="hero-section">
        <div className="container-x relative py-10 md:py-14 lg:py-16 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* Left — compact content */}
          <div className="lg:col-span-5">
            {/* SSK ENTERPRISES — dominant brand line above the headline */}
            <div className="font-display text-[42px] sm:text-[52px] lg:text-[58px] xl:text-[64px] font-black text-[#B5121B] leading-[0.95] tracking-tight mb-4">
              SSK Enterprises
            </div>

            <div className="inline-flex items-center gap-3 mb-4">
              <span className="h-[3px] w-8 bg-[#B5121B]" />
              <span className="text-[11px] font-mono-tech tracking-[0.22em] uppercase font-bold text-[#0F2747]">
                Trusted by Industries. Driven by Quality.
              </span>
            </div>

            <h1 className="font-display text-[26px] sm:text-[30px] lg:text-[32px] xl:text-[34px] font-black text-[#0F2747] leading-[1.15] tracking-tight">
              <span className="text-[#B5121B]">Mr Chemie</span> NDT Products,<br />
              Cold Galvanizing Zinc,<br />
              Industrial Bearings &amp;<br />
              BESSEY Clamps
            </h1>

            <p className="mt-5 text-slate-600 text-[15px] leading-relaxed max-w-lg">
              SSK Enterprises is the Authorised Distributor of 3AK Chemie Pvt. Ltd. for MR Chemie NDT materials and inspection solutions. We also supply KLYDE cold galvanizing zinc products, industrial bearings and BESSEY clamping and cutting tools across Telangana and Andhra Pradesh. We support industries with product selection, technical guidance and prompt quotation support for inspection, maintenance, fabrication and surface protection applications.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button onClick={() => openQuote()} className="btn-accent" data-testid="hero-request-quote">
                Request a Quotation <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/products" className="btn-outline" data-testid="hero-explore">
                Explore Products
              </Link>
            </div>
          </div>

          {/* Right — hero collage: LOCKED 3-panel graphic + separate BESSEY panel beside it */}
          <div className="lg:col-span-7">
            <div className="relative w-full">
              <img
                src="/products/hero-panels-4-final.jpg"
                alt="SSK Enterprises business divisions — MR Chemie NDT Products, KLYDE Cold Galvanizing Zinc, Industrial Bearings, BESSEY Clamping & Cutting Tools"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                className="w-full h-auto block"
                data-testid="hero-panels-image"
              />
              {/* Hotspots — 4 unequal regions matching the composite exactly (locked panel: 0-75.13%, BESSEY: 75.13-100%) */}
              <div className="absolute inset-0">
                <Link to="/products/dye-penetrant-testing"    className="absolute inset-y-0" style={{ left: "0%",     width: "21.13%" }} aria-label="MR Chemie NDT Products" data-testid="hero-hotspot-ndt" />
                <Link to="/products/klyde-zinc-materials"     className="absolute inset-y-0" style={{ left: "21.13%", width: "29.71%" }} aria-label="KLYDE Cold Galvanizing Zinc" data-testid="hero-hotspot-surface" />
                <Link to="/products/industrial-bearings"      className="absolute inset-y-0" style={{ left: "50.84%", width: "24.29%" }} aria-label="Industrial Bearings" data-testid="hero-hotspot-bearings" />
                <Link to="/products/bessey-clamps"            className="absolute inset-y-0" style={{ left: "75.13%", width: "24.87%" }} aria-label="BESSEY Clamping & Cutting Tools" data-testid="hero-hotspot-bessey" />
              </div>
            </div>
          </div>
        </div>

        {/* Industries strip inside the same hero band */}
        <div className="relative border-t border-slate-200 bg-[#F7F8FA]" data-testid="hero-industries-strip">
          <div className="container-x py-5 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 flex-1 gap-x-3 gap-y-3">
              {HERO_INDUSTRIES.map((ind) => {
                const Ic = Icons[ind.icon] || Icons.Factory;
                return (
                  <div key={ind.name} className="flex flex-col items-center text-center gap-1.5" data-testid={`hero-ind-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
                    <Ic className="w-5 h-5 text-[#0F2747]" strokeWidth={1.6} />
                    <span className="text-[10px] font-semibold text-[#0F2747] leading-tight uppercase tracking-wide">{ind.name}</span>
                  </div>
                );
              })}
            </div>
            <div className="text-center lg:text-right lg:pl-6 lg:border-l lg:border-slate-300 lg:min-w-[190px]">
              <div className="font-display text-sm md:text-base font-black text-[#0F2747] tracking-[0.18em] uppercase leading-tight">
                Stronger Today.<br className="hidden lg:block" /> <span className="text-[#B5121B]">Safer Tomorrow.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — Why Industries Choose SSK */}
      <section className="section" data-testid="trust-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-5xl font-black text-[#1E293B] leading-tight">
                Why industries choose <span className="text-[#0F2747]">SSK Enterprises</span>
              </h2>
            </div>
            <p className="text-slate-600 max-w-md">
              Eight reasons buyers across Telangana and Andhra Pradesh place their industrial supply enquiries with us.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUST.map((t, i) => (
              <div key={i} className="p-5 border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#0F2747] transition-colors flex gap-3 items-start" data-testid={`trust-item-${i}`}>
                <div className="w-9 h-9 bg-[#0F2747] text-white flex items-center justify-center flex-shrink-0"><t.icon className="w-4 h-4" /></div>
                <div className="text-sm font-semibold text-[#1E293B] leading-snug pt-1.5">{t.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUR DIVISIONS */}
      <section className="section bg-slate-50 border-y border-slate-200" data-testid="divisions-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-5xl font-black text-[#1E293B] max-w-2xl leading-tight">
                Four focused business divisions
              </h2>
            </div>
            <p className="text-slate-600 max-w-md">
              NDT materials, KLYDE zinc materials, BESSEY clamps and industrial bearings — each with dedicated products and specialist support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <DivisionCard index={0} division={{...DIVISIONS.ndt_materials, slug: "dye-penetrant-testing"}} image={IMG.ndt}
              description="Quality materials, equipment and accessories for penetrant testing, magnetic particle inspection and other industrial inspection requirements."
              cta="View NDT Products" />
            <DivisionCard index={1} division={{...DIVISIONS.klyde_zinc, slug: "klyde-zinc-materials"}} image={IMG.zinc}
              description="Industrial zinc coating and surface-protection products for galvanizing, fabrication, maintenance and corrosion protection — cold galvanizing led."
              cta="View KLYDE Zinc Products" />
            <DivisionCard index={2} division={{...DIVISIONS.bessey, slug: "bessey-clamps"}}
              images={[
                { src: "/products/bessey-clamping.png", label: "Clamping Tools" },
                { src: "/products/bessey-accessories.png", label: "Accessories" },
              ]}
              description="Premium German clamping and cutting tools for fabrication, welding, metalworking, woodworking and industrial applications."
              cta="View BESSEY Products" />
            <DivisionCard index={3} division={DIVISIONS.bearings} image={IMG.bearings}
              description="Industrial bearings for machinery, engineering and maintenance — NTN bearings and other brands available on request." 
              cta="Bearing Enquiry" />
          </div>
        </div>
      </section>

      {/* AUTHORISED DISTRIBUTOR — moved below Industries per user spec */}

      {/* PRODUCT FEATURES */}
      <section className="section section-alt" data-testid="features-section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight">Categories We Supply</h2>
            <p className="text-slate-600 mt-4">Standard and customised solutions for industrial inspection, surface protection and machinery — direct from MR Chemie GmbH, Germany, KLYDE brand lines and leading bearing manufacturers.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="tech-card p-7 h-full flex flex-col" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 flex items-center justify-center" style={{ background: f.color, color: "#fff" }}>
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-[#1E293B] mt-5 leading-tight">{f.title}</h3>
                <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-grow">
                  {f.items.map((it) => (
                    <li key={it} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#B5121B] mt-0.5 flex-shrink-0" /><span>{it}</span></li>
                  ))}
                </ul>
                {f.isBearings && (
                  <>
                    <p className="mt-5 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed italic">{f.note}</p>
                    <button
                      onClick={() => openQuote({ division: "bearings", product_name: "Industrial Bearings Enquiry" })}
                      className="btn-accent w-full mt-4 text-sm"
                      data-testid="feature-bearings-enquire"
                    >
                      {f.cta} <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES GRID — on home page */}
      <section className="section" data-testid="industries-home">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight">Industries we supply</h2>
              <p className="text-slate-600 mt-3 max-w-xl">From aerospace and defence to boiler manufacturing and wind energy — SSK Enterprises supports critical industrial sectors across Telangana and Andhra Pradesh.</p>
            </div>
            <Link to="/industries" className="btn-outline self-start" data-testid="industries-home-cta">
              View all industries <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {INDUSTRIES.slice(0, 15).map((ind) => {
              const Ic = Icons[ind.icon] || Icons.Factory;
              return (
                <div key={ind.name} className="p-4 border border-slate-200 hover:border-[#0F2747] hover:bg-slate-50 transition-colors flex flex-col items-center text-center gap-2">
                  <Ic className="w-5 h-5 text-[#0F2747]" />
                  <span className="text-xs font-semibold text-[#1E293B]">{ind.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AUTHORISED DISTRIBUTOR — premium section with lightbox */}
      <CertificateSection />

      {/* CLOSING TECHNICAL TEAM CTA */}
      <FAQ />
      <section className="section bg-[#0F2747] text-white" data-testid="closing-cta">
        <div className="container-x">
          <div className="max-w-3xl">
            <Building2 className="w-10 h-10 text-[#B5121B]" />
            <h2 className="font-display text-3xl md:text-5xl font-black mt-5 leading-tight">Need help selecting the right product?</h2>
            <p className="text-white/85 mt-5 text-lg leading-relaxed">
              Our team provides technical guidance, product selection assistance and prompt quotation support for your industrial requirements.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button onClick={() => openQuote()} className="btn-accent" data-testid="closing-request-quote">
                Request Quote <ArrowRight className="w-4 h-4" />
              </button>
              <a href={waUrl} target="_blank" rel="noreferrer" className="btn-ghost-light" data-testid="closing-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href={`tel:${phone}`} className="btn-ghost-light" data-testid="closing-call">
                <Phone className="w-4 h-4" /> Call {s.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


/* AUTHORISED DISTRIBUTOR CERTIFICATE section with lightbox / download / fullscreen */
function CertificateSection() {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const thumb = "/certificates/3ak-thumb.jpg";
  const pdf = "/certificates/3ak-authorised-distributor.pdf";

  const bullets = [
    "Official Authorised Distributor of 3AK Chemie",
    "Genuine MR Chemie Products",
    "Cold Galvanizing Zinc Solutions",
    "Industrial Bearings",
    "Technical Product Support",
    "Fast Delivery Across Telangana & Andhra Pradesh",
  ];

  return (
    <section className="section section-alt" data-testid="cert-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 items-center max-w-6xl mx-auto">
          {/* Left — compact authorisation trust card */}
          <div className="lg:col-span-6">
            <div className="bg-white border-l-4 border-[#C8102E] shadow-md p-7 md:p-9">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-[#C8102E] text-white flex items-center justify-center rounded-sm flex-shrink-0">
                  <Icons.ShieldCheck className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div className="inline-flex items-center gap-2 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#0F2747] font-bold">
                  <Icons.BadgeCheck className="w-3.5 h-3.5 text-[#C8102E]" />
                  Verified · 3AK Chemie Pvt. Ltd.
                </div>
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-black text-[#0F2747] leading-tight">Official Authorised Distributor</h2>
              <p className="text-slate-600 mt-4 text-[15px] leading-relaxed">
                SSK Enterprises is officially authorised by 3AK Chemie Pvt. Ltd. to supply MR Chemie NDT products across Telangana and Andhra Pradesh.
              </p>
              <button
                type="button"
                onClick={() => { setScale(1); setOpen(true); }}
                className="btn-accent mt-6"
                data-testid="cert-view-btn"
              >
                <Icons.Eye className="w-4 h-4" /> View Authorisation Certificate
              </button>
            </div>
          </div>

          {/* Right — Why Choose SSK Enterprises (compact) */}
          <div className="lg:col-span-6">
            <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">Why Choose Us</div>
            <h3 className="font-display text-xl md:text-2xl font-black text-[#0F2747] leading-tight mt-2">Why Choose SSK Enterprises</h3>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="cert-why-list">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bg-[#C8102E] text-white mt-0.5">
                    <Icons.Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </span>
                  <span className="text-[#0F2747] font-semibold text-[13.5px] leading-tight">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Lightbox — hidden until View Authorisation Certificate is clicked */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4 md:p-8"
          onClick={() => setOpen(false)}
          data-testid="cert-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Certificate preview"
        >
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Zoom out" data-testid="cert-zoom-out"><Icons.ZoomOut className="w-4 h-4" /></button>
            <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Zoom in" data-testid="cert-zoom-in"><Icons.ZoomIn className="w-4 h-4" /></button>
            <a href={pdf} download className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Download" data-testid="cert-download-lb"><Icons.Download className="w-4 h-4" /></a>
            <button onClick={() => setOpen(false)} className="w-10 h-10 bg-[#C8102E] hover:bg-[#8F0E15] text-white flex items-center justify-center" aria-label="Close" data-testid="cert-close"><Icons.X className="w-4 h-4" /></button>
          </div>
          <div className="max-w-6xl w-full max-h-full overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img
              id="cert-lightbox-img"
              src={thumb}
              alt="SSK Enterprises Authorised Distributor Certificate — 3AK Chemie Pvt. Ltd."
              loading="lazy"
              decoding="async"
              style={{ transform: `scale(${scale})`, transformOrigin: "top center", transition: "transform 200ms ease" }}
              className="w-full h-auto shadow-2xl bg-white"
            />
          </div>
        </div>
      )}
    </section>
  );
}

